import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Send, SlidersHorizontal } from "lucide-react";
import { WebhookDeliveryStatus, WebhookEvent } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatCard } from "@/components/stat-card";
import { StatusBadge } from "@/components/status-badge";
import { CopyButton } from "@/components/copy-button";
import { MerchantCell } from "@/components/merchant-cell";
import { JsonViewer } from "@/components/json-viewer";
import { STATUS_DOT_COLOR, WEBHOOK_DELIVERY_STATUS_COLOR, WEBHOOK_EVENT_COLOR, type StatusColor } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { getWebhookDelivery, listWebhookDeliveries } from "@/api/webhook-deliveries";
import { listMerchants } from "@/api/merchants";
import { type ListWebhookDeliveriesParams, type WebhookDeliveryListItem } from "@/api/types";

// Real implementation of Task 30 Part F, replacing the Task-22 placeholder:
// the GLOBAL, cross-merchant webhook delivery listing (TECH_VIEWER-gated) —
// distinct from the merchant-scoped listing on the merchant detail page
// (Task 25). Same real `useTableState` + `DataTable` shape as
// _app.invoices.tsx/_app.refunds.tsx: server-side pagination + filters, no
// configurable sort (the DTO has none).
type WebhookDeliveryFilters = {
  status?: WebhookDeliveryStatus;
  eventType?: WebhookEvent;
  merchantId?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: WebhookDeliveryFilters = {};

/** Plain type-guarded passthrough — mirrors _app.invoices.tsx's `validateSearch` house style. */
function validateWebhookDeliveriesSearch(
  search: Record<string, unknown>,
): TableSearch<WebhookDeliveryFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    status: typeof search.status === "string" ? (search.status as WebhookDeliveryStatus) : undefined,
    eventType: typeof search.eventType === "string" ? (search.eventType as WebhookEvent) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/webhook-deliveries")({
  validateSearch: validateWebhookDeliveriesSearch,
  component: WebhookDeliveriesPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

/** 2xx = success, 3xx = info, 4xx/5xx = danger, no response yet = neutral. */
function responseCodeColor(code: number): StatusColor {
  if (code >= 200 && code < 300) return "success";
  if (code >= 300 && code < 400) return "info";
  return "danger";
}

/** The plain-language outcome behind a response code — same bucketing as `responseCodeColor`,
 * so the detail dialog's hero stat reads as "404 · Client error" instead of a bare number an
 * operator has to mentally classify themselves. */
function responseCodeOutcome(code: number): string {
  if (code >= 200 && code < 300) return "Success";
  if (code >= 300 && code < 400) return "Redirect";
  if (code >= 400 && code < 500) return "Client error";
  return "Server error";
}

function ResponseCode({ code }: { code: number | null }) {
  if (code === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className="font-semibold"
      style={{ color: STATUS_DOT_COLOR[responseCodeColor(code)], fontVariantNumeric: "tabular-nums" }}
    >
      {code}
    </span>
  );
}

const SELECT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

const STATUS_SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

const INPUT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

// ─── row-detail dialog ──────────────────────────────────────────────────────
//
// Fetch-on-open, same convention as every other detail dialog in this app.

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

/** Uppercase eyebrow label above a group of `DetailRow`s — same treatment used across the app's other detail dialogs. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

/** A tinted callout for the one thing on this dialog that most needs an operator's eye —
 * the last error string a failed/retrying delivery recorded. Free text (whatever the callback
 * endpoint or the fetch itself reported), so it gets its own full-width block rather than being
 * squeezed into a DetailRow's right-aligned value column, which wraps badly for anything longer
 * than a couple of words. */
function ErrorNotice({ message }: { message: string }) {
  const hex = STATUS_DOT_COLOR.danger;
  return (
    <div
      className="flex items-start gap-2 rounded-md p-3 text-sm"
      style={{ backgroundColor: `${hex}0D`, color: hex }}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="break-words">{message}</p>
    </div>
  );
}

function WebhookDeliveryDetailDialog({
  deliveryId,
  onOpenChange,
}: {
  deliveryId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["webhook-delivery", deliveryId],
    queryFn: () => getWebhookDelivery(deliveryId!),
    enabled: deliveryId !== null,
  });

  // One relevant "what happens next / what happened" fact, not two rows that are each null half
  // the time: PENDING has a next-attempt ETA, SUCCEEDED has a delivered time, EXHAUSTED has
  // neither — no more attempts are coming, which is itself the fact worth stating.
  const timelineRow =
    data &&
    (data.status === WebhookDeliveryStatus.SUCCEEDED
      ? { label: "Delivered", value: formatTimestamp(data.deliveredAt) }
      : data.status === WebhookDeliveryStatus.PENDING
        ? { label: "Next attempt", value: formatTimestamp(data.nextAttemptAt) }
        : { label: "Next attempt", value: "None — retries exhausted" });

  return (
    <Dialog open={deliveryId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        {isPending && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {isError && !isPending && (
          <div className="py-10 text-center text-sm text-muted-foreground">Failed to load delivery.</div>
        )}

        {data && !isPending && !isError && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-6">
                <DialogTitle>Webhook delivery</DialogTitle>
                <StatusBadge label={formatStatusLabel(data.status)} color={WEBHOOK_DELIVERY_STATUS_COLOR[data.status]} />
                <StatusBadge label={data.eventType} color={WEBHOOK_EVENT_COLOR[data.eventType]} />
              </div>
              <DialogDescription asChild>
                <div className="flex flex-col gap-0.5">
                  <span>{data.merchantBusinessName}</span>
                  <span className="flex items-center gap-1 font-mono text-xs">
                    {data.invoicePublicId}
                    <CopyButton value={data.invoicePublicId} label="Copy invoice ID" className="h-4 w-4" />
                  </span>
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Response"
                value={<ResponseCode code={data.lastResponseCode} />}
                subLine={data.lastResponseCode === null ? "No response yet" : responseCodeOutcome(data.lastResponseCode)}
                icon={Send}
                tone={data.lastResponseCode === null ? "neutral" : responseCodeColor(data.lastResponseCode)}
              />
              <StatCard label="Attempts" value={data.attemptCount} />
            </div>

            {data.lastError && <ErrorNotice message={data.lastError} />}

            <SectionLabel>Timeline</SectionLabel>
            <dl className="space-y-2">
              <DetailRow label={timelineRow!.label} value={timelineRow!.value} />
            </dl>

            <div className="space-y-2">
              <SectionLabel>Payload</SectionLabel>
              <JsonViewer data={data.payload} defaultCollapsed={false} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────
//
// DataTable has no onRowClick concept — same cell-filling <button> pattern as
// _app.high-risk.tsx's RowButton / -transactions-table.tsx's RowButton.

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

// ─── "More filters" dialog ───────────────────────────────────────────────────
//
// Status stays as an always-visible quick filter in the toolbar; Event type,
// Merchant, and the date range live behind this one button instead of 5
// controls competing for space inline — same pattern -transactions-table.tsx's
// TransactionFiltersDialog established.

function WebhookDeliveryFiltersDialog({
  filters,
  setFilter,
  merchants,
}: {
  filters: WebhookDeliveryFilters;
  setFilter: <K extends keyof WebhookDeliveryFilters>(key: K, value: WebhookDeliveryFilters[K]) => void;
  merchants: Array<{ id: string; businessName: string }>;
}) {
  const [open, setOpen] = useState(false);

  const dialogFilterKeys: Array<keyof WebhookDeliveryFilters> = ["eventType", "merchantId", "from", "to"];
  const activeCount = dialogFilterKeys.filter((key) => filters[key] !== undefined).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Filter webhook deliveries</DialogTitle>
          <DialogDescription>Narrow the list by event type, merchant, or date.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="filter-event-type">Event type</Label>
            <select
              id="filter-event-type"
              className={SELECT_CLASSNAME}
              value={filters.eventType ?? "ALL"}
              onChange={(e) =>
                setFilter("eventType", e.target.value === "ALL" ? undefined : (e.target.value as WebhookEvent))
              }
            >
              <option value="ALL">All event types</option>
              {Object.values(WebhookEvent).map((eventType) => (
                <option key={eventType} value={eventType}>
                  {formatStatusLabel(eventType)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-merchant">Merchant</Label>
            <select
              id="filter-merchant"
              className={SELECT_CLASSNAME}
              value={filters.merchantId ?? "ALL"}
              onChange={(e) => setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All merchants</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.businessName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-from">From date</Label>
            <input
              id="filter-from"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.from ?? ""}
              onChange={(e) => setFilter("from", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-to">To date</Label>
            <input
              id="filter-to"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.to ?? ""}
              onChange={(e) => setFilter("to", e.target.value || undefined)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={activeCount === 0}
            onClick={() => dialogFilterKeys.forEach((key) => setFilter(key, undefined))}
          >
            Clear these filters
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── webhook deliveries page ────────────────────────────────────────────────

function WebhookDeliveriesPage() {
  const tableState = useTableState<WebhookDeliveryFilters>({ defaultFilters: DEFAULT_FILTERS });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params: ListWebhookDeliveriesParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["webhook-deliveries", params],
    queryFn: () => listWebhookDeliveries(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const columns: DataTableColumn<WebhookDeliveryListItem>[] = [
    {
      key: "merchant",
      header: "Merchant",
      // Not a RowButton like the rest of the row (which opens the delivery
      // detail dialog) — this cell navigates to the merchant page instead.
      cell: (d) => (
        <MerchantCell merchantId={d.merchantId} businessName={d.merchantBusinessName} publicId={d.merchantPublicId} />
      ),
    },
    {
      key: "invoicePublicId",
      header: "Invoice",
      cell: (d) => (
        <span className="flex items-center gap-1">
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: d.invoiceId }}
            className="group -m-2 block p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="font-mono text-xs group-hover:underline">{d.invoicePublicId}</span>
          </Link>
          <CopyButton value={d.invoicePublicId} label="Copy invoice ID" className="h-5 w-5 text-muted-foreground hover:text-foreground" />
        </span>
      ),
    },
    {
      key: "eventType",
      header: "Event type",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <StatusBadge label={d.eventType} color={WEBHOOK_EVENT_COLOR[d.eventType]} />
        </RowButton>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <StatusBadge label={formatStatusLabel(d.status)} color={WEBHOOK_DELIVERY_STATUS_COLOR[d.status]} />
        </RowButton>
      ),
    },
    {
      key: "attemptCount",
      header: "Attempts",
      className: "text-right",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{d.attemptCount}</span>
        </RowButton>
      ),
    },
    {
      key: "lastResponseCode",
      header: "Last response",
      className: "text-right",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <ResponseCode code={d.lastResponseCode} />
        </RowButton>
      ),
    },
    {
      key: "nextAttemptAt",
      header: "Next attempt",
      cell: (d) => <RowButton onSelect={() => setSelectedId(d.id)}>{formatTimestamp(d.nextAttemptAt)}</RowButton>,
    },
    {
      key: "deliveredAt",
      header: "Delivered",
      cell: (d) => <RowButton onSelect={() => setSelectedId(d.id)}>{formatTimestamp(d.deliveredAt)}</RowButton>,
    },
  ];

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load webhook deliveries." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={STATUS_SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter(
                  "status",
                  e.target.value === "ALL" ? undefined : (e.target.value as WebhookDeliveryStatus),
                )
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(WebhookDeliveryStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>

            <WebhookDeliveryFiltersDialog
              filters={tableState.filters}
              setFilter={tableState.setFilter}
              merchants={sortedMerchants}
            />

            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(d) => d.id}
        emptyMessage="No webhook deliveries match your filters."
      />

      <WebhookDeliveryDetailDialog
        deliveryId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
